import * as R from 'ramda';
import React, {useEffect, useState} from 'react';
import {map} from 'rxjs/operators';

import {translate} from 'iss/strings';
import {createParty, searchPartiesWithRole} from 'iss/service/PartyService';
import {matchSearchOnProps} from 'iss/utils/search';
import {Field, Select} from 'iss/widgets/Forms';
import {RestResultMessage} from 'iss/widgets/MessageBox';
import {formatParty} from 'iss/widgets/format/Party';

function matchParties(party, search) {
  const matcher = matchSearchOnProps(search, ["firstname", "lastname", "email", "username", "phoneNumber", "jobTitle"]);
  return matcher(party);
}

/* ActivationPartySelector is a PartySelector that allows creating
 * parties only if an associated activation party type selector has
 * the correct value */
export function ActivationPartySelector({partyTypeValuePath, ...props}) {
  return <Field valuePath={partyTypeValuePath}>
    {partyTypeApi =>
      <PartySelector {...props} creatable={canCreateParties(partyTypeApi.getValue())} />
    }
  </Field>;
}

function canCreateParties(partyType) {
  return partyType === 'INFORMANT';
}

export function PartySelector(props) {
  return props.creatable ? <CreatablePartySelector {...props} /> : SimplePartySelector(props);
}

class CreatablePartySelector extends React.Component {
  state = {
    userCreateError: null,
  };
  createParty = (email, callback) => {
    createParty(email)
      .subscribe(party => {
        callback(party);
        this.setState({userCreateError: null});
      }, error => {
        callback(null);
        this.setState({userCreateError: error});
      });
  };
  render() {
    const {excludedParties = [], ...props} = this.props;

    return <React.Fragment>
      <Field valuePath={props.valuePath}>
        {fieldApi => {
          const isEmail = R.is(String);

          // If props.onChange is defined, then without removing it and calling it from the local onChange,
          // spreading props in SimplePartySelector arg list would cause ignoring the local onChange call
          // (which is declared explicitly in SimplePartySelector's arg list).
          const _onChange = props.onChange;
          delete props.onChange;

          const onChange = (selected) => {
            if (this.props.multi) {
              selected.filter(isEmail)
                .forEach(email => this.createParty(email, newParty => {
                  fieldApi.updateValue(value => replaceOrRemove(value, newParty.email, newParty));
                }));
            } else {
              if (selected && isEmail(selected)) {
                this.createParty(selected, fieldApi.setValue);
              }
            }
            if (_onChange) {
              _onChange(selected);
            }
          };
          const isNotExcludedEmail = email => R.none(R.propEq(email, "email"), excludedParties);
          const isValidNewOption = R.both(isValidEmail, isNotExcludedEmail);

          return <SimplePartySelector creatable={true} onChange={onChange} excludedParties={excludedParties}
            promptTextCreator={(label) => translate('evaluationActivator.createParty') + `: ${label}`}
            isValidNewOption={isValidNewOption} {...props} />;
        }}
      </Field>
      <RestResultMessage id={"userCreateError-"+props.id} result={this.state.userCreateError} />
    </React.Fragment>;
  }
}

//Replaces value in list by replacement if it exists. If replacement is null, remove value from list
export function replaceOrRemove(list, value, replacement) {
  const index = list.indexOf(value);

  if (index === -1) return list;

  return replacement ?
    R.update(index, replacement, list) :
    R.remove(index, 1, list);
}

function toPartyOption(party) {
  if (R.is(String, party) || party === null) return party;
  return { value: party, label: formatParty(party) };
}

const emailRegex = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;
function isValidEmail(email) {
  return emailRegex.test(email);
}

function SimplePartySelector({parties = null, excludedParties = [], ...props}) {
  // Debounce based on https://github.com/JedWatson/react-select/issues/614#issuecomment-621579510
  const [debounce, setDebounce] = useState({});
  const [options, setOptions] = useState({});

  useEffect(() => {
    const { cb, delay } = debounce;
    const timeout = setTimeout(cb, delay);
    return () => clearTimeout(timeout);
  }, [debounce]);

  const getPartyOptions = (input) => {
    return searchPartiesWithRole(input, props.includeEmailOnlyUsers)
      .pipe(map((parties) => ({
        options: parties.map(toPartyOption)
      })))
      .toPromise();
  };

  const debouncedLoadOptions = (value, callback) => {
    setDebounce({
      cb: () => {
        try {
          // Without this if no text has been input and user unfocuses -> the same request is sent.
          if (options.input === value) {
            callback(null, options.result);
          } else {
            getPartyOptions(value).then(data => {
              setOptions({input: value, result: data});
              callback(null, data);
            });
          }

        } catch (err) {
          // Ignored
        }
      },
      delay: 500
    });
  };

  // Debounce isn't enabled by default as it doesn't seem to work in PartySelectorsGroup
  // - the typed character always gets cleared.
  const loadOptions = props.useDebounce ? debouncedLoadOptions : getPartyOptions;
  const isNotExcluded = party => R.none(R.propEq(party.id, "id"), excludedParties);
  const optionProps = parties ?
    {options: parties.map(toPartyOption), filterOption: matchParties} :
    {async: true, loadOptions: loadOptions, toOption: toPartyOption, filterOption: isNotExcluded};

  return <Select {...optionProps} cache={false} {...props} />;
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/selectors/PartySelectors.jsx